// Loads the genre index and the picked genres' shards (SPEC F6, Dig by genre). The same rules as the style shards
// (useCrate.ts): fetch online and cache in IndexedDB, fall back to the cached copy offline, and never show data more
// than 30 days old: it is dropped and the status is "expired" until a fresh copy can be fetched.

import { useEffect, useState } from 'react';
import type { Channel, StyleId } from '../../data/types';
import { deleteCrate, loadCrate, saveCrate } from '../../db/repos/dig';
import { afterFirstPaint } from '../../lib/afterFirstPaint';
import { crateExpired, type CrateChannel, type CrateShard } from '../../lib/dig';
import { dropRuntimeCache, fetchJson, isShard, loadChannelRegistry } from './useCrate';

/** public/crate/genres.json: the genres that have a shard, with their record counts */
export interface GenreIndex {
  v: 1;
  builtAt: string;
  fetchedAt?: string;
  genres: [genre: string, records: number][];
}

export const isGenreIndex = (x: unknown): x is GenreIndex =>
  typeof x === 'object' && x !== null && (x as GenreIndex).v === 1 && Array.isArray((x as GenreIndex).genres);

export interface GenreCrateData {
  status: 'loading' | 'ready' | 'empty' | 'expired';
  index: GenreIndex['genres'];
  /** One shard per picked genre that has one */
  shards: { genre: string; shard: CrateShard }[];
  channels: CrateChannel[];
  local: Channel[];
  noCrateAtAll: boolean;
}

const INDEX_KEY = 'genres-index';
/** The crates table is keyed by style id; genre files use `g-<genre>` and the index `genres-index`, so they never collide. */
const asKey = (k: string): StyleId => k as StyleId;

/** One file, online first, cached copy as the last resort, nothing past 30 days (same logic as useCrate). */
async function loadFile<T>(key: string, path: string, valid: (x: unknown) => x is T & { builtAt: string; fetchedAt?: string }): Promise<{ data?: T; expired: boolean }> {
  const fresh = await fetchJson(path);
  let expired = false;
  if (valid(fresh) && !crateExpired(fresh, Date.now())) {
    await saveCrate(asKey(key), fresh, Date.now()).catch(() => undefined);
    return { data: fresh, expired: false };
  }
  expired = valid(fresh);
  const cached = await loadCrate(asKey(key)).catch(() => undefined);
  const copy = cached?.shard;
  let data: T | undefined;
  if (valid(copy)) {
    if (crateExpired(copy, Date.now())) expired = true;
    else if (!expired) data = copy;
  }
  if (expired) {
    await deleteCrate(asKey(key)).catch(() => undefined);
    await dropRuntimeCache();
  }
  return { data, expired };
}

const loading: GenreCrateData = { status: 'loading', index: [], shards: [], channels: [], local: [], noCrateAtAll: false };

/** `enabled` is false in Style mode: nothing is fetched then. */
export function useGenreCrate(genres: readonly string[], enabled: boolean, reloadKey = 0): GenreCrateData {
  const [loaded, setLoaded] = useState<(GenreCrateData & { key: string }) | undefined>();
  const key = `${[...genres].sort().join(',')}:${reloadKey}`;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const cancelStart = afterFirstPaint(() => void (async () => {
      const idx = await loadFile<GenreIndex>(INDEX_KEY, '/crate/genres.json', (x): x is GenreIndex & { builtAt: string } => isGenreIndex(x) && typeof x.builtAt === 'string');
      let expired = idx.expired;
      const shards: GenreCrateData['shards'] = [];
      if (idx.data) {
        const known = new Set(idx.data.genres.map(([g]) => g));
        for (const genre of genres) {
          if (!known.has(genre)) continue;
          const one = await loadFile<CrateShard>(`g-${genre}`, `/crate/g-${genre}.json`, (x): x is CrateShard => isShard(x));
          if (one.expired) expired = true;
          if (one.data) shards.push({ genre, shard: one.data });
        }
      }
      const registry = await loadChannelRegistry();
      if (cancelled) return;
      setLoaded({
        key,
        status: expired ? 'expired' : idx.data ? 'ready' : 'empty',
        index: idx.data?.genres ?? [],
        shards,
        channels: registry.channels,
        local: registry.local,
        noCrateAtAll: registry.noCrateAtAll,
      });
    })());
    return () => {
      cancelled = true;
      cancelStart();
    };
  }, [enabled, key, genres]);

  return enabled && loaded?.key === key ? loaded : loading;
}
